// apps/client/src/hooks/useDormantLoans.ts
//
// Powers the "Dormant — needs review" tab on the Discharges page. Loads dormant
// loans (active_loans_snapshot is_active=FALSE & discharge_date IS NULL) via the
// getDormantLoansReport callable, merges the per-loan review state from Firestore
// (companies/{c}/dormant_resolutions), and exposes a resolve() that calls
// resolveDormantLoan with optimistic UI (the loan drops out on still_active /
// discharged, or is marked reviewed on keep_dormant), then a silent refetch.
import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { functions, db } from '../lib/firebase';

/**
 * Parse a 'YYYY-MM' / 'YYYY-MM-DD' month string into a LOCAL-time Date (first of
 * the month). Avoids the `new Date('YYYY-MM-DD')` UTC-parse → toLocaleDateString
 * off-by-one that renders a month early in negative-UTC-offset browsers (same
 * safe pattern as utils/dataProcessing.ts).
 */
const parseMonthLocal = (s: string | null): Date | null => {
  if (!s) return null;
  const [y, m] = s.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
};

export type DormantResolution = 'still_active' | 'keep_dormant' | 'discharged';

export interface DormantLoanRecord {
  loanId: string;
  brokerId: string | null;
  broker: string | null;
  customerName: string;
  lender: string | null;
  loanAmountCents: number;
  settlementDate: Date | null;
  dormantSince: Date | null;
  /** Last month the loan produced a NON-ZERO commission (the silence basis). */
  lastCommissionDate: Date | null;
  monthsSilent: number | null;
  lossReason: string | null;
  lossNotes: string | null;
  /** Review state from dormant_resolutions; null = not yet reviewed. */
  reviewedResolution: DormantResolution | null;
  reviewedByEmail: string | null;
  reviewedAt: string | null;
}

interface UseDormantLoansParams {
  companyId: string;
  /**
   * When false, the hook is inert — it never calls getDormantLoansReport and
   * returns an empty queue. The caller computes this from the decoupled dormancy
   * gate (Entourage migration Phase 0): dormancy is ON when the tenant is NOT
   * unified OR has featureFlags.dormancy_retirement in {observe, enforce}, so a
   * unified tenant without the flag stays inert. Defaults to true.
   */
  enabled?: boolean;
  brokerId?: string;
  officeId?: string;
  templateId?: string;
  entityCode?: string;
}

interface UseDormantLoansReturn {
  dormantLoans: DormantLoanRecord[];
  loading: boolean;
  error: string | null;
  totalDormantCount: number;
  totalLoanValueCents: number;
  /** Dormant loans with no review yet (the actionable queue). */
  needsReviewCount: number;
  refetch: (opts?: { silent?: boolean }) => Promise<void>;
  /**
   * Resolve one or more dormant loans. notes optional. For a 'discharged'
   * resolution `reason` is REQUIRED (a LossReason key) and is persisted
   * server-side by resolveDormantLoan into lost_loan_reasons (§4.5 repoint) —
   * NO separate setLossReason call. Returns { reasonWriteFailed } so the caller
   * can warn when the discharge committed but the reason write failed
   * (recoverable via the Discharges editor). Throws on a failed transition.
   */
  resolve: (
    loanIds: string[],
    resolution: DormantResolution,
    notes?: string | null,
    reason?: string | null
  ) => Promise<{ reasonWriteFailed?: boolean }>;
}

/**
 * Decoupled dormancy gate (Entourage migration Phase 0). Dormancy surfaces when
 * the tenant is NOT unified OR has featureFlags.dormancy_retirement in {observe,
 * enforce}. So:
 *   - legacy tenant                      → enabled (byte-identical to before)
 *   - unified WITHOUT the flag           → disabled (byte-identical to before)
 *   - unified WITH observe/enforce       → enabled (Entourage keeps its tab post-flip)
 * loanBookMode defaults to legacy when unset. Mirrors the backend gates in
 * getDormantLoansReport / resolveDormantLoan / resolveDormancyMode.
 */
export function isDormancyEnabled(
  loanBookMode: string | undefined | null,
  dormancyFlag: string | undefined | null,
): boolean {
  return (
    (loanBookMode ?? 'legacy') !== 'unified' ||
    dormancyFlag === 'observe' ||
    dormancyFlag === 'enforce'
  );
}

export function useDormantLoans({
  companyId,
  enabled = true,
  brokerId,
  officeId,
  templateId,
  entityCode,
}: UseDormantLoansParams): UseDormantLoansReturn {
  const [dormantLoans, setDormantLoans] = useState<DormantLoanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalDormantCount, setTotalDormantCount] = useState(0);
  const [totalLoanValueCents, setTotalLoanValueCents] = useState(0);

  const fetchDormantLoans = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!companyId || !enabled) {
        setLoading(false);
        return;
      }
      if (!opts?.silent) setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable(functions, 'getDormantLoansReport');
        const result = await fn({ companyId, brokerId, officeId, templateId, entityCode });
        const data = result.data as {
          totalDormantCount: number;
          totalLoanValueCents: number;
          records: Array<{
            loanId: string;
            brokerId: string | null;
            brokerDisplay: string | null;
            customerName: string | null;
            lender: string | null;
            loanAmountCents: number;
            settlementDate: string | null;
            dormantSince: string | null;
            lastCommissionDate: string | null;
            monthsSilent: number | null;
            lossReason: string | null;
            lossNotes: string | null;
          }>;
        };

        // Per-loan review state (Firestore). Small collection (one doc per resolved
        // loan); keyed by loanId. keep_dormant docs stay relevant (still dormant);
        // still_active/discharged loans have already left the feed.
        const reviews = new Map<string, { resolution: DormantResolution; email: string | null; at: string | null }>();
        try {
          const snap = await getDocs(collection(db, `companies/${companyId}/dormant_resolutions`));
          snap.forEach((doc) => {
            const d = doc.data() as { resolution?: DormantResolution; resolvedByEmail?: string; resolvedAt?: string };
            if (d.resolution) {
              reviews.set(doc.id, { resolution: d.resolution, email: d.resolvedByEmail ?? null, at: d.resolvedAt ?? null });
            }
          });
        } catch (e) {
          // Review-state is supplementary; a read failure must not blank the queue.
          console.error('[useDormantLoans] dormant_resolutions read failed (non-fatal)', e);
        }

        const records: DormantLoanRecord[] = (data.records || []).map((r) => {
          const review = reviews.get(r.loanId);
          return {
            loanId: r.loanId,
            brokerId: r.brokerId ?? null,
            broker: r.brokerDisplay ?? null,
            customerName: r.customerName || 'Unknown',
            lender: r.lender,
            loanAmountCents: r.loanAmountCents,
            settlementDate: r.settlementDate ? new Date(r.settlementDate) : null,
            dormantSince: r.dormantSince ? new Date(r.dormantSince) : null,
            lastCommissionDate: parseMonthLocal(r.lastCommissionDate),
            monthsSilent: r.monthsSilent,
            lossReason: r.lossReason ?? null,
            lossNotes: r.lossNotes ?? null,
            reviewedResolution: review?.resolution ?? null,
            reviewedByEmail: review?.email ?? null,
            reviewedAt: review?.at ?? null,
          };
        });

        setDormantLoans(records);
        setTotalDormantCount(data.totalDormantCount ?? records.length);
        setTotalLoanValueCents(data.totalLoanValueCents ?? 0);
      } catch (err: any) {
        console.error('[useDormantLoans] Error:', err);
        if (!opts?.silent) {
          setError(err instanceof Error ? err.message : 'Failed to fetch dormant loans');
          setDormantLoans([]);
          setTotalDormantCount(0);
          setTotalLoanValueCents(0);
        }
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [companyId, enabled, brokerId, officeId, templateId, entityCode]
  );

  useEffect(() => {
    fetchDormantLoans();
  }, [fetchDormantLoans]);

  const refetch = useCallback(
    async (opts?: { silent?: boolean }) => {
      await fetchDormantLoans(opts);
    },
    [fetchDormantLoans]
  );

  const resolve = useCallback(
    async (
      loanIds: string[],
      resolution: DormantResolution,
      notes?: string | null,
      reason?: string | null
    ): Promise<{ reasonWriteFailed?: boolean }> => {
      const fn = httpsCallable(functions, 'resolveDormantLoan');
      // The loss reason for a 'discharged' outcome is now persisted server-side
      // by resolveDormantLoan (§4.5) — pass it in the SAME single call. Only send
      // the key for a discharge (the server forbids it otherwise).
      const result = await fn({
        companyId,
        loanIds,
        resolution,
        notes: notes ?? null,
        ...(resolution === 'discharged' ? { reason: reason ?? undefined } : {}),
      });
      const data = (result.data ?? {}) as { reasonWriteFailed?: boolean };

      // Optimistic: still_active / discharged leave the dormant queue; keep_dormant
      // stays but is now marked reviewed. Authoritative reconcile via silent refetch.
      const idSet = new Set(loanIds);
      setDormantLoans((prev) => {
        if (resolution === 'keep_dormant') {
          const nowIso = new Date().toISOString();
          return prev.map((l) =>
            idSet.has(l.loanId) ? { ...l, reviewedResolution: 'keep_dormant', reviewedAt: nowIso } : l
          );
        }
        return prev.filter((l) => !idSet.has(l.loanId));
      });
      if (resolution !== 'keep_dormant') {
        setTotalDormantCount((c) => Math.max(0, c - loanIds.length));
      }

      await fetchDormantLoans({ silent: true });
      return { reasonWriteFailed: data.reasonWriteFailed };
    },
    [companyId, fetchDormantLoans]
  );

  const needsReviewCount = dormantLoans.filter((l) => l.reviewedResolution == null).length;

  return {
    dormantLoans,
    loading,
    error,
    totalDormantCount,
    totalLoanValueCents,
    needsReviewCount,
    refetch,
    resolve,
  };
}
